import { RefreshCw, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNouvelleVersionDisponible } from '@/hooks/useNouvelleVersionDisponible';

interface NouvelleVersionBannerProps {
  onRefresh: () => void;
  onDismiss: () => void;
  className?: string;
}

/**
 * Bandeau fin qui propose de recharger la page quand une nouvelle version
 * d'Hector est déployée.
 *
 * Il ne recharge JAMAIS de lui-même : l'avocat peut avoir une conversation en
 * cours ou un canvas ouvert, et c'est à lui de choisir le moment. Il peut aussi
 * masquer le bandeau.
 */
export function NouvelleVersionBanner({ onRefresh, onDismiss, className }: NouvelleVersionBannerProps) {
  return (
    <div
      role="status"
      className={cn(
        // Hauteur MINIMALE, pas fixe : sur une fenêtre étroite la phrase passe
        // à la ligne et un `h-8` la faisait déborder sous le contenu de la page.
        'relative flex min-h-8 shrink-0 items-center justify-center gap-2 border-b border-grey-200 bg-white py-1.5 pl-4 pr-9 text-xs text-grey-700',
        className,
      )}
    >
      <RefreshCw className="h-3.5 w-3.5 shrink-0 text-grey-500" aria-hidden="true" />
      {/* Texte et lien dans un même paragraphe : ils se replient comme une
          seule phrase au lieu de s'empiler en deux colonnes étranglées. */}
      <p className="min-w-0 text-center leading-snug">
        Une mise à jour d'Hector est disponible.{' '}
        <button
          type="button"
          onClick={onRefresh}
          className="font-medium text-grey-900 underline underline-offset-2 hover:text-grey-600"
        >
          Cliquez ici pour rafraîchir la page
        </button>
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Masquer ce bandeau"
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-grey-400 hover:bg-grey-100 hover:text-grey-700"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Branche le bandeau sur la détection de version ; ne rend rien sinon. */
export function BanniereNouvelleVersion() {
  const { disponible, masquer } = useNouvelleVersionDisponible();
  if (!disponible) return null;
  return <NouvelleVersionBanner onRefresh={() => window.location.reload()} onDismiss={masquer} />;
}
